import Markdown from 'react-markdown';
import {
    SendRounded,
    StopRounded,
    SupportAgentRounded,
    ThumbDownOutlined,
    ThumbDownRounded,
    ThumbUpOutlined,
    ThumbUpRounded,
    WarningAmberRounded,
} from '@mui/icons-material';
import {
    Box,
    Alert,
    Button,
    Card,
    CardMedia,
    Divider,
    IconButton,
    Link,
    Modal,
    Paper,
    Skeleton,
    Stack,
    TextField,
    Tooltip,
    Typography,
    alpha,
} from '@mui/material';
import { useState, memo, useRef, useCallback, useEffect, useMemo } from 'react';
import { useThrottledValue } from '@/hooks/use-throttled-value';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { useTheme } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import dedent from 'dedent';
import { ActionResponseStatusAi } from '@/ai/enum/action-response-status-ai';
import { HiddenMessageIdPrefix } from '@/ai/enum/hidden-message-id-prefix.enum';
import { useAndieMemory } from '@/providers/andie-memory-provider';
import { useAndie } from '@/providers/andie-provider';
import AndieContextData from '../AndieContextData';
import { ContextData } from '@/ai/types/context-data';
import AndieButton from '../AndieButton';
import { AndieChatExperience, Suggestions } from './AndieChatExperience';
import { AndieChatAgentSelector } from './AndieChatAgentSelector';
import { AndieChatModeSelector } from './AndieChatModeSelector';
import { useAndieChatMessages, useAndieChatControls } from '@/providers/andie-chat-provider';
import AndieActionPresets from '@/components/andie/AndieActionPresets';
import { isToolUIPart, isReasoningUIPart, type UIMessage, type ReasoningUIPart } from 'ai';
import AndieToolInvocation, {
    getToolName,
    isHiddenToolPart,
    type ToolPart,
} from '../tools/AndieToolInvocation';
import AndieThinkingIndicator from './AndieThinkingIndicator';
import AndieThinkingContent from './AndieThinkingContent';
import AndieIconAnimated from './AndieIconAnimated';
import { useThumbSurvey } from '@/hooks/use-post-hog-thumb-survey';
import { AndieHarnessPlan } from '../AndieHarnessPlan';
import { AndieAskUserPrompt } from './AndieAskUserPrompt';
import AndieUsageMeter, { useAndieUsage } from './AndieUsageMeter';
import AndieMarkdown, { andieRemarkPlugins } from './AndieMarkdown';
import { AndiePollAnswers, PollAnswerItem } from './AndiePollAnswers';
import { useQuery } from '@apollo/client';
import { SubscriptionPreviewDocument } from '@/graphql/generated/subscription-preview.generated';
import { toggleChat } from '@/utils/crisp-chat';
import { andieTopupOffer } from '@/utils/andie-topup';

/**
 * Renders the interactive ask-user answer UI just above the composer when the harness is paused for
 * input AND the pause carries prewritten questions. Pauses without questions fall back to the
 * read-only caption inside AndieHarnessPlan, so nothing renders here for them.
 */
const AndieAskUserGate = memo(() => {
    const { harnessAwaiting } = useAndieChatControls();
    if (harnessAwaiting?.reason !== 'needs-input' || !harnessAwaiting.userQuestions?.length) return null;
    return (
        <Stack sx={{ px: 2, pt: 1.5 }}>
            <AndieAskUserPrompt awaiting={harnessAwaiting} />
        </Stack>
    );
});
AndieAskUserGate.displayName = 'AndieAskUserGate';

const AndieChatInput = memo(
    (props: {
        suggestions?: Suggestions;
        inputValueRef: React.MutableRefObject<string>;
        onSend: (message: string) => void;
        onStop: () => void;
        inProgress: boolean;
        ready: boolean;
    }) => {
        const { t } = useTranslation();

        const [value, setValue] = useState(() => props.inputValueRef.current);
        const [focus, setFocus] = useState(false);
        const inputRef = useRef<HTMLInputElement>(null);
        const isComposingRef = useRef(false);

        const skeletonConfig = useMemo(() => {
            if (props.suggestions?.status === ActionResponseStatusAi.EXECUTING) {
                const count = Math.floor(Math.random() * 3) + 2;
                return Array.from({ length: count }).map(() => ({
                    width: `${Math.floor(Math.random() * 45) + 40}%`,
                }));
            }
            return [];
        }, [props.suggestions?.status]);

        const handleValueChange = useCallback(
            (newValue: string) => {
                setValue(newValue);
                props.inputValueRef.current = newValue;
            },
            [props.inputValueRef],
        );

        useEffect(() => {
            if (props.inputValueRef.current === '' && value !== '') {
                setValue('');
            }
        }, [props.inputValueRef, value]);

        const { contextData, clearContextData } = useAndieMemory();
        const { pathname, search } = useLocation();
        const navigate = useNavigate();

        const sendMessage = (forceValue?: string) => {
            if (!props.ready) return;
            const valueToUse = (forceValue ? forceValue : value).trim();
            if (valueToUse) {
                let finalMessage = valueToUse;
                const memoryContextData = contextData.filter((c) => c.data.placement == 'memory');
                const messageContextData = contextData.filter((c) => c.data.placement == 'message');
                // Always include the route the user is currently on as ambient context (no chip), so
                // Andie knows where they already are and can avoid redundant navigation.
                const memoryPayload = [
                    ...memoryContextData.map((c) => ({ key: c.data.key, value: c.data.value })),
                    { key: 'currentRoute', value: `${pathname}${search}` },
                ];

                if (memoryPayload.length > 0 || messageContextData.length > 0) {
                    finalMessage = dedent`${finalMessage}
                <context-data>
                    Please focus this message in the following provided context, you can use any provided info from the context, but focus on the "key" and "data" field of each:
                    ${
                        memoryPayload.length > 0
                            ? `<memory-context>
                        ${JSON.stringify(memoryPayload)}
                    </memory-context>`
                            : ''
                    }
                    ${
                        messageContextData.length > 0
                            ? `<context-json>
                        ${JSON.stringify(messageContextData)}
                    </context-json>`
                            : ''
                    }
                </context-data>`;
                }

                props.onSend(finalMessage);
                handleValueChange('');

                contextData.forEach((c) => {
                    if (c.data.duration === 'temp') {
                        clearContextData(c.data.key);
                    }
                });

                inputRef.current?.focus();
            }
        };

        const { usage } = useAndieUsage();
        const blocked = !!usage?.blocked;

        const { data: subscriptionData } = useQuery(SubscriptionPreviewDocument, {
            skip: !blocked,
        });
        const topup = andieTopupOffer(subscriptionData?.subscriptionPreview?.currency);

        const processing = props.inProgress;
        const disabled = processing || !props.ready || blocked;

        const inputInside = (
            <Stack spacing={1} p={1.5}>
                <AndieContextData contextData={contextData} action="remove" />
                <TextField
                    variant="standard"
                    multiline
                    maxRows={6}
                    fullWidth
                    autoFocus
                    inputRef={inputRef}
                    placeholder={
                        blocked
                            ? t('andie.usage.input-blocked', {
                                  defaultValue: 'Andie limit reached — resets next period',
                              })
                            : t('andie.chat.input.placeholder')
                    }
                    disabled={!props.ready || blocked}
                    onChange={(e) => handleValueChange(e.target.value)}
                    value={value}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey && !disabled && !isComposingRef.current) {
                            e.preventDefault();
                            sendMessage();
                        }
                    }}
                    onCompositionStart={() => {
                        isComposingRef.current = true;
                    }}
                    onCompositionEnd={() => {
                        isComposingRef.current = false;
                    }}
                    onFocus={() => {
                        setFocus(true);
                    }}
                    onBlur={() => {
                        setFocus(false);
                    }}
                    slotProps={{
                        input: {
                            disableUnderline: true,
                        },
                    }}
                />
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    gap={1}
                    sx={{ minWidth: 0 }}
                >
                    <AndieChatModeSelector />
                    <Stack direction="row" alignItems="center" gap={0.5}>
                        <AndieUsageMeter />
                        {processing ? (
                            <Tooltip title={t('andie.chat.input.tooltip.stop')}>
                                <span style={{ display: 'inline-flex' }}>
                                    <IconButton size="small" onClick={props.onStop}>
                                        <StopRounded />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        ) : (
                            <Tooltip
                                title={
                                    blocked
                                        ? t('andie.usage.tooltip.blocked', {
                                              defaultValue: 'Andie usage limit reached',
                                          })
                                        : !props.ready
                                          ? t('andie.chat.input.tooltip.loading', {
                                                defaultValue: 'Preparing the conversation...',
                                            })
                                          : value.trim()
                                            ? t('andie.chat.input.tooltip.send')
                                            : t('andie.chat.input.tooltip.empty')
                                }
                            >
                                <span style={{ display: 'inline-flex' }}>
                                    <IconButton
                                        size="small"
                                        disabled={!value.trim() || !props.ready || blocked}
                                        onClick={() => sendMessage()}
                                        sx={{
                                            color:
                                                value.trim() && props.ready && !blocked
                                                    ? 'andie.main'
                                                    : undefined,
                                        }}
                                    >
                                        <SendRounded />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        )}
                    </Stack>
                </Stack>
            </Stack>
        );

        return (
            <>
                <Divider />
                <Stack>
                    <AndieHarnessPlan />
                    <AndieAskUserGate />
                </Stack>
                <Stack
                    sx={{
                        px: 2,
                        py: 1,
                        gap: 1,
                        width: '100%',
                        minWidth: 0,
                        boxSizing: 'border-box',
                    }}
                >
                    {!processing && props.ready ? (
                        <Stack
                            direction="row"
                            gap={1}
                            flexWrap="wrap"
                            alignItems="center"
                            justifyContent="flex-start"
                            sx={{
                                overflow: 'visible',
                                py: 0.5,
                                width: '100%',
                                minWidth: 0,
                            }}
                        >
                            {props.suggestions?.status !== ActionResponseStatusAi.EXECUTING &&
                            props.suggestions?.data
                                ? props.suggestions.data
                                      .sort((a, b) => a.label.length - b.label.length)
                                      .map((suggestion, index) => (
                                          <AndieButton
                                              key={index}
                                              title={suggestion.fullMessage}
                                              size="small"
                                              onClick={() => {
                                                  if (suggestion.action === 'injectMessage') {
                                                      handleValueChange(suggestion.fullMessage);
                                                      const input = inputRef.current;
                                                      if (input) {
                                                          input.focus();
                                                          requestAnimationFrame(() => {
                                                              const len = suggestion.fullMessage.length;
                                                              input.setSelectionRange(len, len);
                                                          });
                                                      }
                                                  } else {
                                                      sendMessage(suggestion.fullMessage);
                                                  }
                                              }}
                                              sx={{ textAlign: 'left' }}
                                          >
                                              {suggestion.label}
                                          </AndieButton>
                                      ))
                                : props.suggestions?.status === ActionResponseStatusAi.EXECUTING
                                  ? skeletonConfig.map((config, idx) => (
                                        <Skeleton
                                            key={idx}
                                            variant="rectangular"
                                            height={12}
                                            width={config.width}
                                            sx={{ borderRadius: 2 }}
                                        />
                                    ))
                                  : null}
                        </Stack>
                    ) : null}

                    {blocked && (
                        <Box
                            sx={(theme) => ({
                                borderRadius: 2,
                                p: 2,
                                bgcolor: alpha(theme.palette.primary.main, 0.05),
                                border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
                            })}
                        >
                            <Stack spacing={2}>
                                <Stack spacing={0.75}>
                                    <Stack direction="row" alignItems="center" spacing={0.75}>
                                        <WarningAmberRounded sx={{ fontSize: 16, color: 'primary.main' }} />
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{ fontWeight: 600, lineHeight: 1.2 }}
                                        >
                                            {t('andie.usage.limit-reached-title', {
                                                defaultValue: "You've reached your ANDIE usage limit",
                                            })}
                                        </Typography>
                                    </Stack>
                                    <Typography
                                        variant="subtitle2"
                                        color="text.primary"
                                        sx={{ fontWeight: 700, lineHeight: 1.4, textWrap: 'balance' }}
                                    >
                                        {t('andie.usage.upsell-headline', {
                                            defaultValue:
                                                "But you don't have to wait for the next renewal. Get +{credits} more credits for just {price}",
                                            credits: topup.credits,
                                            price: topup.price,
                                        })}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{ lineHeight: 1.5, textWrap: 'pretty' }}
                                    >
                                        {t('andie.usage.upsell-subtitle', {
                                            defaultValue:
                                                'A quick upgrade, no plan change. Our team activates it in minutes.',
                                        })}
                                    </Typography>
                                </Stack>
                                <Stack spacing={1}>
                                    <Button
                                        variant="contained"
                                        fullWidth
                                        disableElevation
                                        startIcon={<SupportAgentRounded />}
                                        onClick={() => toggleChat(true, 'chat')}
                                        sx={{ py: 1, fontWeight: 600 }}
                                    >
                                        {t('str.help.feature.chat-support', {
                                            defaultValue: 'Chat with support',
                                        })}
                                    </Button>
                                    <Typography variant="caption" color="text.secondary" textAlign="center">
                                        {t('andie.usage.change-plan-prompt', {
                                            defaultValue: 'Prefer to change plan?',
                                        })}{' '}
                                        <Link
                                            component="button"
                                            type="button"
                                            variant="caption"
                                            underline="always"
                                            color="inherit"
                                            sx={{ fontWeight: 600 }}
                                            onClick={() => navigate('/settings/workspace/billing#upgrade')}
                                        >
                                            {t('andie.usage.view-plans', { defaultValue: 'View plans' })}
                                        </Link>
                                    </Typography>
                                </Stack>
                            </Stack>
                        </Box>
                    )}

                    <Card
                        sx={(theme) => ({
                            borderRadius: 2,
                            ...(focus && !processing && { border: `1px solid ${theme.palette.andie.main}` }),
                            ...(processing && {
                                position: 'relative',
                                '&:after': {
                                    content: '""',
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    height: '100%',
                                    width: '100%',
                                    background: `linear-gradient(60deg, ${theme.palette.andie.gradient.join(', ')})`,
                                    borderRadius: 2,
                                    animation: 'animated-gradient 8s ease alternate infinite',
                                    backgroundSize: '300% 300%',
                                },
                                '@keyframes animated-gradient': {
                                    '0%': { backgroundPosition: '0% 50%' },
                                    '50%': { backgroundPosition: '100% 50%' },
                                    '100%': { backgroundPosition: '0% 50%' },
                                },
                                '&:before': {
                                    content: '""',
                                    position: 'absolute',
                                    top: '2px',
                                    left: '2px',
                                    height: 'calc(100% - 4px)',
                                    width: 'calc(100% - 4px)',
                                    bgcolor: theme.palette.background.paper,
                                    borderRadius: 1.5,
                                    zIndex: 1,
                                },
                            }),
                            '& textarea:focus-visible, & input:focus-visible': {
                                outline: 'none',
                            },
                        })}
                    >
                        <Stack sx={{ position: 'relative', zIndex: 2 }}>{inputInside}</Stack>
                    </Card>
                </Stack>
            </>
        );
    },
);

const MarkdownContent = memo(({ text }: { text: string }) => <AndieMarkdown text={text} />);

const FILTER_ARG_KEYS = [
    'since',
    'until',
    'search',
    'netProfileAuthIds',
    'stages',
    'types',
    'assignedToIds',
    'labelsIds',
] as const;

const INTERNAL_STRUCTURED_OUTPUT_KEYS = new Set([
    'copy',
    'copyVariableUuid',
    'elements',
    'briefing',
    'briefingVariableUuid',
    'body',
    'bodies',
    'bodiesVariableUuids',
    'image',
    'images',
    'medias',
]);

const INTERNAL_PROMPT_TAGS = [
    'task',
    'platform_constraints',
    'rules',
    'content_type_guidelines',
    'forbidden',
    'context',
    'briefing',
    'media_count_limit',
    'brand_logo_rules',
    'logo_reference_rules',
    'text_in_image',
    'best_practices',
    'video_rules',
    'suggested_aspect_ratio',
    'number_of_images',
] as const;

const HIDDEN_MESSAGE_ID_PREFIXES = Object.values(HiddenMessageIdPrefix);

const isHiddenChatMessage = (message: UIMessage) =>
    message.id === 'hidden' || HIDDEN_MESSAGE_ID_PREFIXES.some((prefix) => message.id.startsWith(prefix));

const isLikelyToolArgsText = (text: string) => {
    const trimmed = text.trim();
    if (!trimmed || (!trimmed.startsWith('{') && !trimmed.startsWith('['))) return false;

    try {
        const parsed = JSON.parse(trimmed);
        if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return false;

        const keys = Object.keys(parsed);
        return (
            keys.length > 0 &&
            keys.every((key) => FILTER_ARG_KEYS.includes(key as (typeof FILTER_ARG_KEYS)[number]))
        );
    } catch {
        return false;
    }
};

const isLikelyInternalStructuredOutputText = (text: string) => {
    const blocks = text
        .trim()
        .split(/\r?\n+/)
        .map((line) => line.trim())
        .filter(Boolean);
    if (blocks.length === 0 || !blocks.every((block) => block.startsWith('{') && block.endsWith('}'))) {
        return false;
    }

    return blocks.every((block) => {
        try {
            const parsed = JSON.parse(block);
            if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return false;
            const keys = Object.keys(parsed);
            return keys.length > 0 && keys.some((key) => INTERNAL_STRUCTURED_OUTPUT_KEYS.has(key));
        } catch {
            return false;
        }
    });
};

const isLikelyInternalPromptText = (text: string) => {
    const normalized = text.trim().toLowerCase();
    if (!normalized) return false;

    const matchingTags = INTERNAL_PROMPT_TAGS.filter(
        (tag) => normalized.includes(`<${tag}`) || normalized.includes(`</${tag}>`),
    ).length;

    return (
        matchingTags >= 3 ||
        (matchingTags >= 2 &&
            (normalized.includes('<task') ||
                normalized.includes('<platform_constraints') ||
                normalized.includes('<briefing')))
    );
};

const getInternalPromptStartIndex = (text: string) => {
    const normalized = text.toLowerCase();
    return INTERNAL_PROMPT_TAGS.reduce<number>((earliest, tag) => {
        const index = normalized.indexOf(`<${tag}`);
        if (index === -1) return earliest;
        return earliest === -1 ? index : Math.min(earliest, index);
    }, -1);
};

const stripInternalPromptTail = (text: string) => {
    const startIndex = getInternalPromptStartIndex(text);
    if (startIndex === -1) return text;

    const promptTail = text.slice(startIndex);
    return isLikelyInternalPromptText(promptTail) ? text.slice(0, startIndex).trimEnd() : text;
};

const shouldSuppressAssistantTextPart = (text: string, hasToolPart: boolean) =>
    (hasToolPart && isLikelyToolArgsText(text)) ||
    isLikelyInternalStructuredOutputText(text) ||
    isLikelyInternalPromptText(text);

const getDisplayableTextPart = (text: string, hasToolPart: boolean) => {
    const displayText = stripInternalPromptTail(text);
    return shouldSuppressAssistantTextPart(displayText, hasToolPart) ? '' : displayText;
};

const AssistantTextPart = memo(({ text }: { text: string }) => {
    const [previewModal, setPreviewModal] = useState<string | boolean>(false);
    const theme = useTheme();
    const throttledText = useThrottledValue(text, 80);

    if (!throttledText) return null;

    return (
        <Stack>
            <Typography component="span">
                <MarkdownContent text={throttledText} />
            </Typography>
            <Modal
                open={!!previewModal}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
                onClose={() => setPreviewModal(false)}
            >
                <CardMedia
                    component={'img'}
                    src={typeof previewModal === 'string' ? previewModal : ''}
                    sx={{
                        maxHeight: '90%',
                        maxWidth: '90%',
                        width: 'auto',
                        height: 'auto',
                        backgroundColor: theme.palette.text.disabled,
                    }}
                />
            </Modal>
        </Stack>
    );
});

const ANDIE_SURVEY_ID = import.meta.env.VITE_POSTHOG_ANDIE_SURVEY_ID;

const AndieChatFeedback = memo(({ message }: { message: UIMessage }) => {
    const { t } = useTranslation();
    const aiTraceId = (message.metadata as { aiTraceId?: string } | undefined)?.aiTraceId;
    const surveyProperties = useMemo(() => ({ $ai_trace_id: aiTraceId }), [aiTraceId]);
    const { respond, response, triggerRef } = useThumbSurvey({
        surveyId: ANDIE_SURVEY_ID,
        disableAutoShownTracking: true,
        properties: surveyProperties,
    });

    if (!aiTraceId) return null;

    const voted = Boolean(response);
    const iconSx = { transition: 'color 0.15s ease' };

    return (
        <Stack direction="row" gap={1} ref={triggerRef}>
            <Tooltip title={t('andie.feedback.helpful', { defaultValue: 'Helpful' })} placement="bottom">
                <IconButton
                    size="medium"
                    aria-label={t('andie.feedback.helpful', { defaultValue: 'Helpful' })}
                    aria-pressed={response === 'up'}
                    aria-disabled={voted}
                    onClick={() => !voted && respond('up')}
                    sx={{ color: response === 'up' ? 'success.dark' : 'text.secondary' }}
                >
                    {response === 'up' ? (
                        <ThumbUpRounded fontSize="small" sx={iconSx} />
                    ) : (
                        <ThumbUpOutlined fontSize="small" sx={iconSx} />
                    )}
                </IconButton>
            </Tooltip>
            <Tooltip
                title={t('andie.feedback.notHelpful', { defaultValue: 'Not helpful' })}
                placement="bottom"
            >
                <IconButton
                    size="medium"
                    aria-label={t('andie.feedback.notHelpful', { defaultValue: 'Not helpful' })}
                    aria-pressed={response === 'down'}
                    aria-disabled={voted}
                    onClick={() => !voted && respond('down')}
                    sx={{ color: response === 'down' ? 'error.dark' : 'text.secondary' }}
                >
                    {response === 'down' ? (
                        <ThumbDownRounded fontSize="small" sx={iconSx} />
                    ) : (
                        <ThumbDownOutlined fontSize="small" sx={iconSx} />
                    )}
                </IconButton>
            </Tooltip>
        </Stack>
    );
});

type ReasoningGroup = { kind: 'reasoning-group'; parts: ReasoningUIPart[]; startIndex: number };
type PassthroughItem = { kind: 'passthrough'; part: UIMessage['parts'][number]; index: number };
type RenderItem = ReasoningGroup | PassthroughItem;

function groupReasoningParts(parts: UIMessage['parts']): RenderItem[] {
    const result: RenderItem[] = [];
    let i = 0;
    while (i < parts.length) {
        const part = parts[i];
        if (isReasoningUIPart(part) && (part.text || '') !== '') {
            const group: ReasoningUIPart[] = [];
            const startIndex = i;
            while (i < parts.length && isReasoningUIPart(parts[i])) {
                group.push(parts[i] as ReasoningUIPart);
                i++;
            }
            result.push({ kind: 'reasoning-group', parts: group, startIndex });
        } else {
            result.push({ kind: 'passthrough', part, index: i });
            i++;
        }
    }
    return result;
}

const AndieChatAssistantMessage = memo(
    ({ message, isStreaming }: { message: UIMessage; isStreaming: boolean }) => {
        const hasToolPart = message.parts.some((p) => isToolUIPart(p));
        const hasContent = message.parts.some(
            (p) =>
                isReasoningUIPart(p) ||
                (p.type === 'text' && p.text && getDisplayableTextPart(p.text, hasToolPart)) ||
                (isToolUIPart(p) && !isHiddenToolPart(p as ToolPart)),
        );
        // A paused askUser turn is not a rateable response — suppress the feedback survey when the
        // message carries a hidden tool part (e.g. askUser).
        const hasHiddenTool = message.parts.some((p) => isToolUIPart(p) && isHiddenToolPart(p as ToolPart));

        const renderItems = useMemo(() => groupReasoningParts(message.parts), [message.parts]);

        if (!hasContent) return null;

        return (
            <Stack py={2} gap={1} sx={{ wordBreak: 'break-word' }}>
                {renderItems.map((item) => {
                    if (item.kind === 'reasoning-group') {
                        return (
                            <AndieThinkingContent key={`reasoning-${item.startIndex}`} parts={item.parts} />
                        );
                    }
                    const { part, index } = item;
                    if (part.type === 'text' && part.text) {
                        const displayText = getDisplayableTextPart(part.text, hasToolPart);
                        if (!displayText.trim()) {
                            return null;
                        }
                        return <AssistantTextPart key={`text-${index}`} text={displayText} />;
                    }
                    if (isToolUIPart(part)) {
                        if (isHiddenToolPart(part as ToolPart)) return null;
                        return (
                            <AndieToolInvocation
                                key={part.toolCallId || `tool-${index}`}
                                part={part}
                                isActive={isStreaming}
                            />
                        );
                    }
                    return null;
                })}
                {!isStreaming && !hasHiddenTool && <AndieChatFeedback message={message} />}
            </Stack>
        );
    },
);

const AndieChatUserMessage = memo(({ message, isFirst }: { message: UIMessage; isFirst?: boolean }) => {
    const textContent = message.parts
        .filter((p) => p.type === 'text')
        .map((p) => p.text)
        .join('');

    if (!textContent || isHiddenChatMessage(message)) return null;

    // An ask-user submission arrives as a <poll-answers> JSON payload; render it as an answers card
    // instead of the raw tag. Falls through to normal text rendering if the payload doesn't parse.
    const pollMatch = textContent.trim().match(/^<poll-answers>([\s\S]*)<\/poll-answers>$/);
    if (pollMatch) {
        try {
            const items = JSON.parse(pollMatch[1]) as PollAnswerItem[];
            if (Array.isArray(items) && items.length > 0) {
                return (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.4 }}
                        style={{ display: 'block' }}
                    >
                        <Stack pt={isFirst ? 4 : 0} pb={3} alignItems="flex-end">
                            <AndiePollAnswers items={items} />
                        </Stack>
                    </motion.div>
                );
            }
        } catch {
            // fall through to normal text rendering
        }
    }

    let contextData: ContextData[] = [];
    const contextDataMatch = textContent.match(/<context-json>([\s\S]*?)<\/context-json>/);
    if (contextDataMatch) {
        try {
            contextData = JSON.parse(contextDataMatch[1]);
        } catch {
            contextData = [];
        }
    }

    const displayText = stripInternalPromptTail(
        textContent.replace(
            /(<context-message>[\s\S]*?<\/context-message>|<context-data>[\s\S]*?<\/context-data>|<hidden-message>[\s\S]*?<\/hidden-message>)/g,
            '',
        ),
    );

    if (!displayText.trim()) return null;

    return (
        <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4 }}
            style={{ display: 'inline-block' }}
        >
            <Stack pt={isFirst ? 4 : 0} pb={3} gap={1}>
                <Paper
                    sx={{
                        px: 2,
                        maxWidth: '80%',
                        alignSelf: 'flex-end',
                        backgroundColor: 'andie.main',
                        color: 'primary.contrastText',
                        borderRadius: 4,
                        borderTopRightRadius: 4,
                        wordBreak: 'break-word',
                    }}
                >
                    <Typography component="span">
                        <Markdown
                            remarkPlugins={andieRemarkPlugins}
                            components={{
                                a: ({ children, href, ...props }) => (
                                    <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
                                        {children}
                                    </a>
                                ),
                            }}
                        >
                            {displayText}
                        </Markdown>
                    </Typography>
                </Paper>
                {contextData.length > 0 && (
                    <Stack alignItems="flex-end">
                        <AndieContextData contextData={contextData} action="add" />
                    </Stack>
                )}
            </Stack>
        </motion.div>
    );
});

const AndieEmptyState = memo(() => {
    const { t } = useTranslation();
    return (
        <Stack flex={1} alignItems="center" justifyContent="center" py={4} gap={2}>
            <AndieIconAnimated size={48} />
            <Stack alignItems="center" gap={0.5}>
                <Typography variant="h6" fontWeight={600}>
                    {t('andie.empty.title')}
                </Typography>
                <Typography variant="body2" color="text.secondary" textAlign="center">
                    {t('andie.empty.subtitle')}
                </Typography>
            </Stack>
            <AndieActionPresets />
        </Stack>
    );
});

export const AndieChatMessages = memo(() => {
    const { messages } = useAndieChatMessages();
    const { isLoading, status } = useAndieChatControls();
    const { t } = useTranslation();
    const scrollRef = useRef<HTMLDivElement>(null);

    const lastMsg = messages[messages.length - 1];
    const lastMsgIsAssistant = lastMsg?.role === 'assistant';
    const lastAssistantPart = lastMsgIsAssistant ? (lastMsg as UIMessage).parts.at(-1) : undefined;
    const showThinking =
        isLoading && (!lastMsgIsAssistant || (!!lastAssistantPart && isToolUIPart(lastAssistantPart)));
    const showThinkingTextAgent = isLoading && !!lastAssistantPart && !showThinking;
    const thinkingToolName =
        lastAssistantPart &&
        isToolUIPart(lastAssistantPart) &&
        !isHiddenToolPart(lastAssistantPart as ToolPart)
            ? getToolName(lastAssistantPart as ToolPart)
            : undefined;

    const [isAtBottom, setIsAtBottom] = useState(true);
    const isAtBottomRef = useRef(true);

    const scrollToBottom = useCallback(() => {
        const container = scrollRef.current;
        if (!container || !isAtBottomRef.current) return;
        requestAnimationFrame(() => {
            container.scrollTop = container.scrollHeight;
        });
    }, []);

    useEffect(() => {
        const container = scrollRef.current;
        if (!container) return;
        const onScroll = () => {
            const { scrollTop, scrollHeight, clientHeight } = container;
            const atBottom = scrollTop + clientHeight >= scrollHeight - 50;
            if (atBottom !== isAtBottomRef.current) {
                isAtBottomRef.current = atBottom;
                setIsAtBottom(atBottom);
            }
        };
        container.addEventListener('scroll', onScroll, { passive: true });
        return () => container.removeEventListener('scroll', onScroll);
    }, []);

    useEffect(() => {
        scrollToBottom();
    }, [messages, scrollToBottom]);

    useEffect(() => {
        const container = scrollRef.current;
        if (!container) return;
        const observer = new ResizeObserver(scrollToBottom);
        observer.observe(container);
        return () => observer.disconnect();
    }, [scrollToBottom]);

    const prevIsLoadingRef = useRef(false);
    useEffect(() => {
        if (!prevIsLoadingRef.current && isLoading) {
            isAtBottomRef.current = true;
            setIsAtBottom(true);
            scrollToBottom();
        }
        prevIsLoadingRef.current = isLoading;
    }, [isLoading, scrollToBottom]);

    return (
        <Stack sx={{ flex: 1, minHeight: 0 }}>
            <Stack
                ref={scrollRef}
                sx={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: 'auto',
                    px: 2,
                }}
            >
                {messages.length === 0 && !isLoading && <AndieEmptyState />}
                {messages.map((message, index) =>
                    message.role === 'assistant' ? (
                        <AndieChatAssistantMessage
                            key={message.id}
                            message={message}
                            isStreaming={isLoading && index === messages.length - 1}
                        />
                    ) : message.role === 'user' ? (
                        <AndieChatUserMessage key={message.id} message={message} isFirst={index === 0} />
                    ) : null,
                )}
                {isLoading && isAtBottom && (
                    <AndieThinkingIndicator textWriting={showThinkingTextAgent} toolName={thinkingToolName} />
                )}
                {status === 'error' && (
                    <Stack py={2}>
                        <Alert severity="warning" variant="outlined" sx={{ borderRadius: 1 }}>
                            <Typography variant="body2" fontWeight={600}>
                                {t('andie.chat.error.title', {
                                    defaultValue: "Andie couldn't finish that request",
                                })}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {t('andie.chat.error.description', {
                                    defaultValue:
                                        'The request stopped before Andie could complete it. Any content already saved is still available. Please try again in a moment.',
                                })}
                            </Typography>
                        </Alert>
                    </Stack>
                )}
                <div />
            </Stack>
            {isLoading && !isAtBottom && (
                <Box sx={{ px: 2 }}>
                    <AndieThinkingIndicator textWriting={showThinkingTextAgent} toolName={thinkingToolName} />
                </Box>
            )}
        </Stack>
    );
});

const AndieChat = memo(() => {
    const [suggestions, setSuggestions] = useState<Suggestions>({
        status: ActionResponseStatusAi.COMPLETE,
        data: [],
    });
    const { inputValueRef } = useAndie();
    const { sendMessage, stop, isLoading, isHydrated } = useAndieChatControls();

    return (
        <>
            <AndieChatAgentSelector />
            <AndieChatExperience setSuggestions={setSuggestions} />
            <AndieChatMessages />
            <AndieChatInput
                suggestions={suggestions}
                inputValueRef={inputValueRef}
                onSend={sendMessage}
                onStop={stop}
                inProgress={isLoading}
                ready={isHydrated}
            />
        </>
    );
});

export default AndieChat;
